import * as Sentry from '@sentry/tanstackstart-react';

import { localStorage } from '#utils/localStorage';

/**
 * Кто сидит за этим экраном — для отчётов об ошибках, без ожидания ответа сервера.
 *
 * Профиль приходит запросом `me`, а самый массовый класс ошибок — сетевой: при нём `me` как раз
 * и не загружается, и до этой правки такие ошибки уезжали в GlitchTip без владельца (25.08:
 * сбой DNS на Android, поле user пустое). Поэтому личность запоминается при загрузке профиля и
 * восстанавливается на старте следующего сеанса, до первого запроса.
 *
 * Хранилище — на устройство, а не на аккаунт: выход из аккаунта её стирает, вход другим
 * аккаунтом перезаписывает.
 */

const KEY = 'sentry-identity';
const NO_SESSION = 'none';

export interface SentryIdentity {
  id: string;
  username?: string;
  /** Ключ сеанса из реестра устройств; по нему поддержка находит ошибки конкретного устройства. */
  sessionId?: string;
}

export const applyIdentity = (identity: SentryIdentity | null) => {
  Sentry.setUser(identity ? { id: identity.id, username: identity.username } : null);
  Sentry.setTag('session_id', identity?.sessionId ?? NO_SESSION);
};

export const rememberIdentity = (identity: SentryIdentity) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(identity));
  } catch {
    // Хранилище закрыто — личность проживёт только до перезагрузки, это не ошибка.
  }
};

export const forgetIdentity = () => {
  localStorage.deleteItem(KEY);
  Sentry.setTag('session_id', NO_SESSION);
};

export const readIdentity = (): SentryIdentity | null => {
  const raw = localStorage.getItem(KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<SentryIdentity> | null;
    if (!parsed || typeof parsed.id !== 'string' || !parsed.id) {
      return null;
    }
    return {
      id: parsed.id,
      username: typeof parsed.username === 'string' ? parsed.username : undefined,
      sessionId: typeof parsed.sessionId === 'string' ? parsed.sessionId : undefined,
    };
  } catch {
    return null;
  }
};

/** На старте: применить сохранённую личность, если она есть. */
export const restoreIdentity = (): SentryIdentity | null => {
  const identity = readIdentity();
  if (identity) {
    applyIdentity(identity);
  }
  return identity;
};
